import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  IEventoGenerico,
  IPopulate,
  IQueryParam,
  IQueryTripero,
  IReporteBase,
  IReporteLuminariaACTISEnergia,
  IReporteTracker4G,
  IValoresEventoTracker,
  ModoConsumoVehiculo,
  IRendimientoCombustibleVehiculo,
  Stop,
  Trip,
} from 'modelos/src';
import { Lista2Service } from '../../../../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../../../../auxiliares/servicios/helper.service';
import {
  IZonaPaso,
  EstiloRecorrido,
  LineStringMapaVehiculos,
  PointLineStringVehiculo,
} from '../../../interfaces';
import { COLOR_REPORTE, COLOR_VIAJE } from '../../../colores-viaje';
import * as XLSX from 'xlsx-js-style';
import * as FileSaver from 'file-saver';

export interface TripStatsConfig {
  mostrarEventos: boolean;
  tiempoDetenidoMinutos: number | null;
  velocidadExceso: number | null;
}

export interface TripStats {
  loadingParadas?: boolean;
  loadingExceso?: boolean;
  loadingEventos?: boolean;
  eventCount?: number;
  stopCount?: number;
  excessSpeedCount?: number;
}

export interface ExcelExportConfig {
  vehiculoId: string;
  nombreBase: string;
  idEntidad: string;
  mostrarEventos: boolean;
  tiempoDetenidoMinutos: number | null;
  velocidadExceso: number | null;
  mostrarZonas?: boolean;
  zonasEnViaje?: IZonaPaso[];
  /** Consumo del sensor de combustible en el rango (null = sin sensor) */
  consumo?: {
    datos: IRendimientoCombustibleVehiculo;
    modo: ModoConsumoVehiculo;
  } | null;
}

type ReporteTracker = IReporteBase<
  'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
>;

/**
 * Distancia y velocidades de un recorrido, tal como se muestran en el panel y
 * en el Excel. `null` = sin dato (nunca 0: un 0 se lee como "no se movió").
 */
export interface MetricasRecorrido {
  /** Metros */
  distanciaM: number | null;
  /** km/h */
  velocidadPromedio: number | null;
  /** km/h */
  velocidadMaxima: number | null;
}

const METRICAS_VACIAS: MetricasRecorrido = {
  distanciaM: null,
  velocidadPromedio: null,
  velocidadMaxima: null,
};

/** Por encima de esto un tramo es un salto de GPS/odómetro, no un viaje. */
const VELOCIDAD_IMPOSIBLE_KMH = 200;

@Injectable({ providedIn: 'root' })
export class RecorridosService {
  private lista2 = inject(Lista2Service);
  private helper = inject(HelperService);

  // ── PURE HELPERS ─────────────────────────────────────────────────────────────

  static getDuracionViaje(startTime: string, endTime: string): string {
    if (!startTime || !endTime) return 'N/A';
    const duration =
      new Date(endTime).getTime() - new Date(startTime).getTime();
    if (duration <= 0) return 'N/A';
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }

  static formatearMinutos(minutos: number): string {
    if (!minutos || minutos <= 0) return '0 min';
    const hours = Math.floor(minutos / 60);
    const mins = minutos % 60;
    if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    return `${mins} min`;
  }

  static formatearDuracion(durationSec: number): string {
    const durationMs = durationSec * 1000;
    if (!durationMs || durationMs <= 0) return 'N/A';
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  static findTripByDate(trips: Trip[], fecha: string): Trip | undefined {
    if (!trips || trips.length === 0) return undefined;
    const targetDate = new Date(fecha).getTime();
    return trips.find((trip) => {
      if (!trip.startTime || !trip.endTime) return false;
      return (
        targetDate >= new Date(trip.startTime).getTime() &&
        targetDate <= new Date(trip.endTime).getTime()
      );
    });
  }

  // ── MÉTRICAS DE RECORRIDO ────────────────────────────────────────────────────
  //
  // Panel y Excel salen de estos helpers y de ningún otro lado, para que la
  // pantalla y la planilla no puedan mostrar números distintos. Un dato que
  // falta es null y se muestra como "Sin dato" / '-', nunca como 0.

  /**
   * Métricas de un viaje de tripero. Se usan los valores de tripero tal cual;
   * sólo si la velocidad promedio viene en 0 o falta, se la calcula con la
   * distancia y la duración del mismo viaje (misma definición que tripero).
   * Un 0 se toma como "sin dato": un viaje sin distancia ni duración es un
   * viaje que tripero no llegó a medir, no uno detenido.
   */
  static metricasDeViaje(trip: Trip | null | undefined): MetricasRecorrido {
    if (!trip) return METRICAS_VACIAS;
    const distanciaM = RecorridosService.positivo(trip.distance);
    const duracionS =
      RecorridosService.positivo(trip.duration) ??
      RecorridosService.segundosEntre(trip.startTime, trip.endTime);
    const velocidadPromedio =
      RecorridosService.positivo(trip.averageSpeed) ??
      (distanciaM != null && duracionS != null
        ? (distanciaM / duracionS) * 3.6
        : null);
    return {
      distanciaM,
      velocidadPromedio,
      velocidadMaxima: RecorridosService.positivo(trip.maxSpeed),
    };
  }

  /**
   * Métricas de una búsqueda por fecha, sólo con los reportes (sin consultar
   * a tripero).
   *
   * - Distancia: odómetro; si el equipo no lo informa, coordenadas.
   * - Velocidad promedio: promedio de las velocidades > 0 de los reportes.
   * - Velocidad máxima: la mayor reportada.
   *
   * Todo es una sola pasada por los reportes.
   */
  static metricasDeFecha(reportes: ReporteTracker[]): MetricasRecorrido {
    let suma = 0;
    let cantidad = 0;
    let maxima = 0;
    for (const r of reportes ?? []) {
      const vel = (r?.valores as IReporteTracker4G)?.velocidad;
      if (vel == null || !(vel > 0)) continue;
      suma += vel;
      cantidad++;
      if (vel > maxima) maxima = vel;
    }
    return {
      distanciaM:
        RecorridosService.calcularDistanciaPorOdometro(reportes) ??
        RecorridosService.calcularDistanciaPorCoordenadas(reportes),
      velocidadPromedio: cantidad ? suma / cantidad : null,
      velocidadMaxima: cantidad ? maxima : null,
    };
  }

  /**
   * Distancia por odómetro, sumando sólo los avances entre reportes válidos.
   *
   * Antes era último − primero, y un 0 o un null en cualquiera de las dos
   * puntas (equipo recién reiniciado, reporte sin odómetro) anulaba el día
   * entero. Ahora:
   * - se ignoran los odómetros nulos o ≤ 0;
   * - un valor repetido (odómetro congelado) no suma ni rompe nada;
   * - si el odómetro retrocede (reinicio del equipo) se toma el valor nuevo
   *   como base, sin restar;
   * - un avance imposible (> 200 km/h desde la última vez que cambió) se
   *   descarta como basura y se rebasa. Se mide desde el último cambio y no
   *   desde el reporte anterior para no descartar el salto legítimo que da
   *   un odómetro que estuvo congelado un rato.
   *
   * Necesita al menos dos odómetros válidos; si no, null (el que llama
   * decide el fallback).
   */
  static calcularDistanciaPorOdometro(
    reportes: ReporteTracker[],
  ): number | null {
    if (!reportes || reportes.length < 2) return null;
    let validos = 0;
    let total = 0;
    let base: number | null = null;
    let tBase: number | null = null;
    for (const r of reportes) {
      const odo = (r?.valores as IReporteTracker4G)?.odometro;
      if (odo == null || !(odo > 0)) continue;
      validos++;
      const t = RecorridosService.tiempoReporte(r);
      if (base == null) {
        base = odo;
        tBase = t;
        continue;
      }
      if (odo === base) continue; // congelado: se mantiene el tiempo del cambio
      if (odo > base) {
        const delta = odo - base;
        const dtS = t != null && tBase != null ? (t - tBase) / 1000 : null;
        const imposible =
          dtS != null &&
          dtS > 0 &&
          (delta / dtS) * 3.6 > VELOCIDAD_IMPOSIBLE_KMH;
        if (!imposible) total += delta;
      }
      // Avance válido, salto imposible o retroceso: el valor nuevo es la base
      base = odo;
      tBase = t;
    }
    if (validos < 2) return null;
    return total;
  }

  /**
   * Distancia por coordenadas: haversine entre reportes consecutivos. Es el
   * fallback cuando el equipo no informa odómetro. Se descartan los tramos
   * que implicarían más de 200 km/h (saltos de GPS) porque uno solo de esos
   * agrega decenas de kilómetros que nunca se recorrieron. Un punto anómalo
   * aislado no se toma como base del tramo siguiente; si se descartan varios
   * seguidos, el anómalo era el anterior y se rebasa en el actual.
   */
  static calcularDistanciaPorCoordenadas(
    reportes: ReporteTracker[],
  ): number | null {
    let total = 0;
    let tramos = 0;
    let previo: { coord: number[]; t: number | null } | null = null;
    let descartadosSeguidos = 0;
    for (const r of reportes ?? []) {
      const coord = (r?.valores as IReporteTracker4G)?.geojson?.coordinates;
      if (coord?.length !== 2) continue;
      const actual = { coord, t: RecorridosService.tiempoReporte(r) };
      if (previo) {
        const distanciaM = RecorridosService.haversineM(previo.coord, coord);
        const dtS =
          actual.t != null && previo.t != null
            ? (actual.t - previo.t) / 1000
            : null;
        const imposible =
          dtS != null &&
          dtS > 0 &&
          (distanciaM / dtS) * 3.6 > VELOCIDAD_IMPOSIBLE_KMH;
        if (imposible) {
          if (++descartadosSeguidos >= 3) {
            previo = actual;
            descartadosSeguidos = 0;
          }
          continue;
        }
        descartadosSeguidos = 0;
        total += distanciaM;
        tramos++;
      }
      previo = actual;
    }
    return tramos ? total : null;
  }

  private static haversineM(a: number[], b: number[]): number {
    const rad = Math.PI / 180;
    const [lon1, lat1] = a;
    const [lon2, lat2] = b;
    const dLat = (lat2 - lat1) * rad;
    const dLon = (lon2 - lon1) * rad;
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
    return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /** Momento del reporte en ms (timestamp del genérico, o fecha del equipo). */
  private static tiempoReporte(r: ReporteTracker): number | null {
    const v = r?.valores as IReporteTracker4G & { timestamp?: string };
    const fecha = v?.timestamp ?? v?.fechaDevice ?? r?.fechaCreacion;
    if (!fecha) return null;
    const ms = new Date(fecha).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  private static positivo(n: number | null | undefined): number | null {
    return n != null && Number.isFinite(n) && n > 0 ? n : null;
  }

  private static segundosEntre(desde?: string, hasta?: string): number | null {
    if (!desde || !hasta) return null;
    const s = (new Date(hasta).getTime() - new Date(desde).getTime()) / 1000;
    return Number.isFinite(s) && s > 0 ? s : null;
  }

  static armarRecorridos(
    coordsVialPath: [number, number][],
    coordsSnapped: [number, number][],
    reportes: IReporteBase<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >[],
    estilo?: EstiloRecorrido,
    usarSnapped = false,
  ): LineStringMapaVehiculos[] {
    // Cada punto sale de SU reporte (posición, fecha y velocidad juntas). Antes
    // se emparejaba coordsSnapped[i] con reportes[i], pero son dos consultas
    // distintas: el vialPath descarta los reportes sin posición y, si el
    // filtro no acota el tipo, suma los de combustible. Con un largo distinto
    // las fechas quedaban corridas y los puntos de más salían "Sin fecha" a
    // 0 km/h. coordsSnapped queda solo si no llegaron reportes.
    const pointsInfo: PointLineStringVehiculo[] = reportes.length
      ? reportes.flatMap((r) => {
          const v = r.valores as IReporteTracker4G & { timestamp?: string };
          const coord =
            (usarSnapped ? v?.snappedGeojson?.coordinates : undefined) ??
            v?.geojson?.coordinates;
          if (coord?.length !== 2) return [];
          return [
            {
              coordinate: [coord[0], coord[1]] as [number, number],
              fechaDevice: v?.fechaDevice ?? v?.timestamp ?? '',
              velocidad: v?.velocidad ?? 0,
            },
          ];
        })
      : coordsSnapped.map((coord) => ({
          coordinate: coord,
          fechaDevice: '',
          velocidad: 0,
        }));
    return [
      {
        pointsInfo,
        pointsRecorrido: coordsVialPath,
        // Por defecto, colores fijos (no el del tema del cliente) para que la
        // traza no se confunda con los markers de paradas / exceso / eventos.
        // El panel puede pisarlos.
        color: estilo?.colorLinea || COLOR_VIAJE,
        colorPuntos: estilo?.colorReportes || COLOR_REPORTE,
        radioPuntos: estilo?.tamanoReportes,
        verTraza: estilo?.verTraza ?? true,
        verReportes: estilo?.verReportes ?? true,
        modoColor: estilo?.modoColorTraza ?? 'plano',
        velocidadRelativa: !!estilo?.escalaVelocidadRelativa,
        animada: !!estilo?.trazaAnimada,
        colorMovimiento: estilo?.colorMovimiento,
        colorDegradadoInicio: estilo?.colorDegradadoInicio,
        desplazar: estilo?.separarIdaYVuelta ?? true,
        // Punteada va un poco más fina: con el mismo ancho, los guiones se ven
        // más pesados que la línea llena.
        width: estilo?.estiloLinea === 'punteada' ? 4 : 5,
        dash: estilo?.estiloLinea === 'punteada',
        arrow: true,
        trip: true,
      },
    ];
  }

  // ── API HELPERS ──────────────────────────────────────────────────────────────

  async obtenerConteoEventos(
    startTime: string,
    endTime: string,
    idVehiculo: string,
  ): Promise<number> {
    const data = await firstValueFrom(
      this.lista2.eventosGenericos(
        {
          sort: '-fechaCreacion',
          filter: JSON.stringify({
            idEntidad: idVehiculo,
            fechaCreacion: { $gte: startTime, $lte: endTime },
            categoria: { $ne: 'Posición' },
          }),
          includeChildren: true,
        },
        0,
      ),
    );
    return (data?.datos || []).filter((e) => e.categoria !== 'Posición').length;
  }

  async obtenerConteoParadas(
    startTime: string,
    endTime: string,
    idVehiculo: string,
    tiempoDetenidoMinutos: number,
  ): Promise<number> {
    const query: IQueryTripero = { idVehiculo, from: startTime, to: endTime };
    const data = await firstValueFrom(this.lista2.paradas(query));
    if (!data) return 0;
    return data.filter(
      (p) => Math.floor(p.duration / 60) >= tiempoDetenidoMinutos,
    ).length;
  }

  async obtenerConteoExceso(
    startTime: string,
    endTime: string,
    idVehiculo: string,
    velocidadExceso: number,
  ): Promise<number> {
    const query: IQueryParam = {
      filter: JSON.stringify({
        tipoEntidad: 'Vehiculo',
        idsAsignados: idVehiculo,
        'valores.timestamp': { $gte: startTime, $lte: endTime },
        'valores.velocidad': { $gte: velocidadExceso },
      }),
      select: 'valores.velocidad',
      sort: 'valores.timestamp',
      includeChildren: true,
      // El conteo se hace sobre los datos traídos: con el limit por defecto
      // (1000) los excesos de un rango largo quedaban subcontados.
      limit: 0,
      unlimited: true,
    };
    const reportes = await firstValueFrom(
      this.lista2.reportesGenericos<
        'Tracker 4G' | 'Tracker Qualcomm' | 'Tracker T1000B'
      >(['Tracker 4G', 'Tracker Qualcomm', 'Tracker T1000B'], query),
    );
    return reportes.datos.length;
  }

  async calcularStatsParaViaje(
    trip: Trip,
    idVehiculo: string,
    config: TripStatsConfig,
  ): Promise<TripStats> {
    if (!trip.startTime || !trip.endTime) return {};
    const stats: TripStats = {};
    const promises: Promise<void>[] = [];

    if (config.mostrarEventos) {
      promises.push(
        this.obtenerConteoEventos(trip.startTime, trip.endTime, idVehiculo)
          .then((c) => {
            stats.eventCount = c;
          })
          .catch(() => {
            stats.eventCount = 0;
          }),
      );
    }
    if ((config.tiempoDetenidoMinutos ?? 0) > 0) {
      promises.push(
        this.obtenerConteoParadas(
          trip.startTime,
          trip.endTime,
          idVehiculo,
          config.tiempoDetenidoMinutos!,
        )
          .then((c) => {
            stats.stopCount = c;
          })
          .catch(() => {
            stats.stopCount = 0;
          }),
      );
    }
    if ((config.velocidadExceso ?? 0) > 0) {
      promises.push(
        this.obtenerConteoExceso(
          trip.startTime,
          trip.endTime,
          idVehiculo,
          config.velocidadExceso!,
        )
          .then((c) => {
            stats.excessSpeedCount = c;
          })
          .catch(() => {
            stats.excessSpeedCount = 0;
          }),
      );
    }

    await Promise.all(promises);
    return stats;
  }

  // ── EXCEL EXPORT ─────────────────────────────────────────────────────────────

  async generarExcelBlob(
    startTime: string,
    endTime: string,
    config: ExcelExportConfig,
    metricas?: MetricasRecorrido,
  ): Promise<Blob> {
    const { vehiculoId, nombreBase, idEntidad, mostrarEventos } = config;
    const mostrarDetenido = (config.tiempoDetenidoMinutos ?? 0) > 0;
    const mostrarExceso = (config.velocidadExceso ?? 0) > 0;

    const populate: IPopulate[] = [
      { path: 'activo', select: 'identificacion' },
      { path: 'cliente', select: 'nombre' },
    ];

    const [reportesData, eventosData, paradasData] = await Promise.all([
      firstValueFrom(
        this.lista2.reportesGenericos<
          'Tracker 4G' | 'Tracker Qualcomm' | 'Tracker T1000B'
        >(['Tracker 4G', 'Tracker Qualcomm', 'Tracker T1000B'], {
          filter: JSON.stringify({
            tipoEntidad: 'Vehiculo',
            idsAsignados: vehiculoId,
            'valores.timestamp': { $gte: startTime, $lte: endTime },
          }),
          select:
            'fechaCreacion idsAsignados idCliente valores.timestamp valores.fechaDevice valores.geojson valores.velocidad valores.orientacion valores.ignicion valores.odometro valores.direccion valores.triperoState',
          sort: 'valores.timestamp',
          page: 0,
          limit: 0,
          unlimited: true,
          includeChildren: true,
          populate: JSON.stringify(populate),
        }),
      ),
      mostrarEventos
        ? firstValueFrom(
            this.lista2.eventosGenericos(
              {
                sort: '-fechaCreacion',
                limit: 500,
                filter: JSON.stringify({
                  idEntidad,
                  fechaCreacion: { $gte: startTime, $lte: endTime },
                  categoria: { $ne: 'Posición' },
                }),
                includeChildren: true,
                childrenLevel: 0,
              },
              0,
            ),
          )
        : Promise.resolve(null),
      mostrarDetenido
        ? firstValueFrom(
            this.lista2.paradas({
              idVehiculo: vehiculoId,
              from: startTime,
              to: endTime,
            }),
          ).catch(() => [] as Stop[])
        : Promise.resolve([] as Stop[]),
    ]);

    const reportes = reportesData?.datos ?? [];
    const eventos = mostrarEventos
      ? (eventosData?.datos ?? []).filter(
          (e: IEventoGenerico) => e.categoria !== 'Posición',
        )
      : [];
    let paradas = (Array.isArray(paradasData) ? paradasData : []) as Stop[];
    if (mostrarDetenido) {
      paradas = paradas.filter(
        (p) =>
          Math.floor(p.duration / 60) >= (config.tiempoDetenidoMinutos ?? 0),
      );
    }

    // Las métricas vienen del panel (viaje o fecha) para que el Excel diga lo
    // mismo que la pantalla. Sólo si nadie las pasó se calculan acá, con el
    // mismo helper que la búsqueda por fecha.
    const { distanciaM, velocidadPromedio, velocidadMaxima } =
      metricas ?? RecorridosService.metricasDeFecha(reportes);

    const umbralExceso = mostrarExceso ? config.velocidadExceso! : 0;
    const excesoPuntos = mostrarExceso
      ? reportes
          .filter((r) => {
            const vel = this.helper.getValores(r)?.velocidad;
            return vel != null && vel >= umbralExceso;
          })
          .map((r) => {
            const v = this.helper.getValores(r);
            return {
              Fecha: this._fmtDate(r.fechaCreacion),
              'Velocidad (km/h)': v?.velocidad ?? '-',
              Latitud: v?.geojson?.coordinates?.[1] ?? '-',
              Longitud: v?.geojson?.coordinates?.[0] ?? '-',
              Dirección: v?.direccion ?? '-',
            };
          })
      : [];

    // Hoja 1: General
    const generalRow: Record<string, any> = {
      'Período inicio': this._fmtDate(startTime),
      'Período fin': this._fmtDate(endTime),
      Duración: RecorridosService.getDuracionViaje(startTime, endTime),
      'Distancia (km)':
        distanciaM != null ? +(distanciaM / 1000).toFixed(2) : '-',
      // Redondeo igual que el panel (pipe number '1.0-0')
      'Vel. Promedio (km/h)':
        velocidadPromedio != null ? Math.round(velocidadPromedio) : '-',
      'Vel. Máxima (km/h)':
        velocidadMaxima != null ? Math.round(velocidadMaxima) : '-',
      'Total Reportes': reportes.length,
    };
    if (mostrarEventos) generalRow['Total Eventos'] = eventos.length;
    if (mostrarDetenido)
      generalRow[`Total Paradas (≥${config.tiempoDetenidoMinutos} min)`] =
        paradas.length;
    if (mostrarExceso)
      generalRow[`Total Exc. Vel. (≥${umbralExceso} km/h)`] =
        excesoPuntos.length;
    if (config.mostrarZonas)
      generalRow['Total Zonas visitadas'] = config.zonasEnViaje?.length ?? 0;
    if (config.consumo)
      Object.assign(generalRow, this._columnasConsumo(config.consumo));

    const orientaciones = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
    const fmtOrientacion = (deg?: number) =>
      deg != null ? orientaciones[Math.round(deg / 45) % 8] : '-';

    // Hoja 2: Reportes
    const wsReportes = XLSX.utils.json_to_sheet(
      reportes.map((r) => {
        const v = this.helper.getValores(r);
        return {
          Fecha: this._fmtDate(r.fechaCreacion),
          Cliente: r?.cliente?.nombre ?? '-',
          Vehículo: r?.activo?.identificacion ?? '-',
          'Fecha Device': this._fmtDate(v?.fechaDevice || v?.timestamp),
          Latitud: v?.geojson?.coordinates?.[1] ?? '-',
          Longitud: v?.geojson?.coordinates?.[0] ?? '-',
          'Velocidad (km/h)': v?.velocidad ?? '-',
          Orientación: fmtOrientacion(v?.orientacion),
          Ignición: v?.ignicion != null ? (v.ignicion ? 'Sí' : 'No') : '-',
          // Un odómetro ≤ 0 es falta de dato (equipo sin odómetro o recién
          // reiniciado), no un vehículo en el kilómetro cero.
          'Odómetro (km)':
            (v as IReporteTracker4G)?.odometro > 0
              ? +((v as IReporteTracker4G).odometro / 1000).toFixed(2)
              : '-',
          Estado: (v as IReporteTracker4G)?.triperoState ?? '-',
          Dirección: v?.direccion ?? '-',
        };
      }),
    );

    const sheetsToProcess: XLSX.WorkSheet[] = [
      XLSX.utils.json_to_sheet([generalRow]),
      wsReportes,
    ];
    const sheetNames: string[] = ['General', 'Reportes'];

    if (mostrarEventos) {
      sheetsToProcess.push(
        XLSX.utils.json_to_sheet(
          eventos.length > 0
            ? eventos.map((e: IEventoGenerico) => {
                const val = e.valores as IValoresEventoTracker;
                return {
                  Fecha: this._fmtDate(e.fechaCreacion),
                  Categoría: e.categoria ?? '-',
                  'Tipo Evento': e.tipoEvento ?? '-',
                  Título: val?.titulo ?? '-',
                  Mensaje:
                    val?.mensaje !== val?.titulo ? (val?.mensaje ?? '-') : '-',
                  Latitud: val?.geojson?.coordinates?.[1] ?? '-',
                  Longitud: val?.geojson?.coordinates?.[0] ?? '-',
                  Dirección: val?.direccion ?? '-',
                  Estado: e.estado ?? '-',
                };
              })
            : [{ Nota: 'Sin eventos en el período' }],
        ),
      );
      sheetNames.push('Eventos');
    }

    if (mostrarDetenido) {
      sheetsToProcess.push(
        XLSX.utils.json_to_sheet(
          paradas.length > 0
            ? paradas.map((p) => ({
                'Fecha Inicio': this._fmtDate(p.startTime),
                'Fecha Fin': this._fmtDate(p.endTime),
                Duración: RecorridosService.formatearDuracion(p.duration),
                'Duración (min)': Math.floor(p.duration / 60),
                Latitud: p.latitude ?? '-',
                Longitud: p.longitude ?? '-',
                Dirección: p.address ?? '-',
              }))
            : [{ Nota: 'Sin paradas en el período' }],
        ),
      );
      sheetNames.push('Detenido');
    }

    if (mostrarExceso) {
      sheetsToProcess.push(
        XLSX.utils.json_to_sheet(
          excesoPuntos.length > 0
            ? excesoPuntos
            : [
                {
                  Nota: `Sin registros de exceso de velocidad (≥${umbralExceso} km/h)`,
                },
              ],
        ),
      );
      sheetNames.push('Exceso Velocidad');
    }

    if (config.mostrarZonas) {
      const zonas = config.zonasEnViaje ?? [];
      sheetsToProcess.push(
        XLSX.utils.json_to_sheet(
          zonas.length > 0
            ? zonas.map((z: IZonaPaso) => ({
                Zona: z.ubicacion?.identificacion || 'Sin nombre',
                Entró: this._fmtDate(z.entro),
                Salió: z.salio
                  ? this._fmtDate(z.salio)
                  : 'Sin salida registrada',
                'Duración (min)': z.duracionMinutos ?? 0,
                Dirección: (z.ubicacion as any)?.direccion ?? '-',
              }))
            : [{ Nota: 'No se detectaron zonas en el período' }],
        ),
      );
      sheetNames.push('Zonas');
    }

    sheetsToProcess.forEach(this._autoFitColumns);
    sheetsToProcess.forEach(this._applyHeaderStyle);

    const sheets: Record<string, XLSX.WorkSheet> = {};
    sheetNames.forEach((name, i) => {
      sheets[name] = sheetsToProcess[i];
    });

    const excelBuffer = XLSX.write(
      { Sheets: sheets, SheetNames: sheetNames },
      { bookType: 'xlsx', type: 'array', cellStyles: true },
    );
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return blob;
  }

  async exportarExcel(
    startTime: string,
    endTime: string,
    config: ExcelExportConfig,
    metricas?: MetricasRecorrido,
  ): Promise<void> {
    const blob = await this.generarExcelBlob(
      startTime,
      endTime,
      config,
      metricas,
    );
    const dateStr = new Date(startTime)
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, '');
    FileSaver.saveAs(blob, `${config.nombreBase}_viaje_${dateStr}.xlsx`);
  }

  // ── EXCEL HELPERS PRIVADOS ───────────────────────────────────────────────────

  /** Columnas de consumo de combustible para la hoja General. */
  private _columnasConsumo(consumo: {
    datos: IRendimientoCombustibleVehiculo;
    modo: ModoConsumoVehiculo;
  }): Record<string, number | string> {
    const r = consumo.datos;
    const n = (v?: number, dec = 1) => (v != null ? +v.toFixed(dec) : '-');
    const cols: Record<string, number | string> = {
      'Consumo total (L)': n(r.litrosConsumidos),
    };
    if (consumo.modo === 'hora') {
      cols['Horas de motor'] = n(r.horasMotor);
      cols['Consumo prom. (L/h)'] = n(r.rendimientoLh);
    } else {
      cols['Consumo prom. (L/100km)'] = n(r.rendimientoL100km);
      cols['Rendimiento (km/L)'] = n(r.rendimientoKmL, 2);
    }
    cols['Nivel inicial (L)'] = n(r.nivelInicial);
    cols['Nivel final (L)'] = n(r.nivelFinal);
    cols['Cargas'] = r.cantidadCargas ?? 0;
    cols['Litros cargados'] = n(r.litrosCargados);
    if (r.cantidadDescargas) {
      cols['Descargas'] = r.cantidadDescargas;
      cols['Litros descargados'] = n(r.litrosDescargados);
    }
    if (r.motivoSinDatos && !r.datosSuficientes) {
      cols['Observación consumo'] = r.motivoSinDatos;
    }
    return cols;
  }

  private _fmtDate(d?: string): string {
    if (!d) return '-';
    return new Date(d).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  private _getPastelPrimaryRgb(): string {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--theme-primary-500')
      .trim();
    let r = 0,
      g = 0,
      b = 0;
    const hexMatch = raw.match(/^#?([0-9a-fA-F]{6})$/);
    if (hexMatch) {
      r = parseInt(hexMatch[1].slice(0, 2), 16);
      g = parseInt(hexMatch[1].slice(2, 4), 16);
      b = parseInt(hexMatch[1].slice(4, 6), 16);
    } else {
      const rgbMatch = raw.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        r = +rgbMatch[1];
        g = +rgbMatch[2];
        b = +rgbMatch[3];
      }
    }
    const mix = (c: number) => Math.round(c * 0.25 + 255 * 0.75);
    return [mix(r), mix(g), mix(b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }

  private _applyHeaderStyle = (ws: XLSX.WorkSheet): void => {
    const pastelRgb = this._getPastelPrimaryRgb();
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    for (let col = range.s.c; col <= range.e.c; col++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[addr]) continue;
      ws[addr].s = {
        fill: { patternType: 'solid', fgColor: { rgb: pastelRgb } },
        font: { bold: true },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  };

  private _autoFitColumns = (ws: XLSX.WorkSheet): void => {
    const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
    if (!data.length) return;
    const colCount = data.reduce((max, row) => Math.max(max, row.length), 0);
    const widths: { wch: number }[] = [];
    for (let col = 0; col < colCount; col++) {
      const maxLen = data.reduce((max, row) => {
        const cell = row[col];
        return Math.max(max, cell != null ? String(cell).length : 0);
      }, 0);
      widths.push({ wch: Math.min(Math.max(maxLen + 2, 10), 60) });
    }
    ws['!cols'] = widths;
  };
}
